import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useAccounts } from '../../context/AccountsContext';
import type { MeteredUsage, MeteredUsageAccount, PaymentsSummary, TurnBillingUsage, TurnBillingUsageAccount } from '../../api/client';

const DAY_MS = 86_400_000;

/** Everything the Payments page reads on load. Usage figures are fetched next to the
 *  money-critical summary and may arrive a beat later; a failed usage read leaves its
 *  value `null` so the UI shows a dash rather than a misleading $0. */
export function usePaymentsData() {
  const [summary, setSummary] = useState<PaymentsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [metered, setMetered] = useState<MeteredUsage | null>(null);
  /** On-demand usage over the trailing 30 days (Accounts column + rate ladder). */
  const [turn30, setTurn30] = useState<TurnBillingUsage | null>(null);

  const { refreshSubscriptions } = useAccounts();

  // Every plan change on this page ends in a summary reload; the sidebar's plan
  // pills follow it.
  const reloadSummary = useCallback(async () => {
    const s = await api.getPaymentsSummary();
    setSummary(s);
    void refreshSubscriptions();
    return s;
  }, [refreshSubscriptions]);

  const reloadMetered = useCallback(async () => {
    const m = await api.getMeteredUsage().catch(() => null);
    if (m) setMetered(m);
    return m;
  }, []);

  const reloadTurn = useCallback(async () => {
    const now = Date.now();
    const u = await api
      .getTurnBillingUsage({ start: new Date(now - 30 * DAY_MS).toISOString(), end: new Date(now).toISOString() })
      .catch(() => null);
    if (u) setTurn30(u);
    return u;
  }, []);

  const reloadAll = useCallback(async () => {
    await Promise.all([reloadSummary().catch(() => null), reloadMetered(), reloadTurn()]);
  }, [reloadSummary, reloadMetered, reloadTurn]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await api.getPaymentsSummary();
        if (alive) { setSummary(s); setLoadError(''); }
      } catch (e) {
        if (alive) setLoadError(e instanceof Error ? e.message : 'load');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    void reloadMetered();
    void reloadTurn();
    return () => { alive = false; };
  }, [reloadMetered, reloadTurn]);

  const meteredBy: Record<string, MeteredUsageAccount> = {};
  metered?.accounts.forEach((a) => { meteredBy[a.account_id] = a; });
  const turnBy: Record<string, TurnBillingUsageAccount> = {};
  turn30?.accounts.forEach((a) => { turnBy[a.account_id] = a; });

  return {
    summary, setSummary, loading, loadError,
    metered, setMetered, meteredBy,
    turn30, turnBy,
    reloadSummary, reloadMetered, reloadTurn, reloadAll,
  };
}

export type PaymentsData = ReturnType<typeof usePaymentsData>;
